import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Hint } from '../ui/tooltip';
import { LaneDot } from '../items/Tags';
import { FACET_LABELS, TOGGLE_LABELS, countFilters } from '../../lib/filters';
import { DIAGNOSES, EXIT_LANES, PRIORITIES, UNIVERSES } from '../../lib/taxonomy';
import FilterInfo from './FilterInfo';
import { fmtInt } from '../../lib/format';
import { cn } from '../../lib/utils';

const FACET_ORDER = ['diagnosis', 'exit_lane', 'priority', 'universe', 'category', 'brand', 'color', 'material'];
const DESCRIPTIONS = { diagnosis: DIAGNOSES, exit_lane: EXIT_LANES, priority: PRIORITIES, universe: UNIVERSES };
const OPEN_BY_DEFAULT = ['diagnosis', 'exit_lane', 'priority', 'category', 'brand'];
const SEARCHABLE_ABOVE = 10;
const VISIBLE = 8;

function Group({ title, count, defaultOpen = true, children, onClear, info }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b py-3 last:border-b-0">
      <div className="flex items-center justify-between">
        <button type="button" onClick={() => setOpen((o) => !o)} className="flex flex-1 items-center gap-1.5 text-left text-[13px] font-medium">
          <ChevronDown className={cn('h-3.5 w-3.5 text-muted-foreground transition-transform', !open && '-rotate-90')} />
          {title}
          {count > 0 && <span className="rounded-full bg-primary px-1.5 text-[10px] font-semibold leading-4 text-primary-foreground">{count}</span>}
        </button>
        {info}
        {count > 0 && onClear && (
          <button type="button" onClick={onClear} className="text-[11px] text-muted-foreground hover:text-foreground">
            Clear
          </button>
        )}
      </div>
      {open && <div className="mt-2">{children}</div>}
    </div>
  );
}

function FacetGroup({ facetKey, options = [], selected = [], onChange, vocab = [] }) {
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);
  const desc = DESCRIPTIONS[facetKey];

  const merged = useMemo(() => {
    const map = new Map();
    options.forEach((o) => map.set(o.value, o.count));
    selected.forEach((v) => !map.has(v) && map.set(v, 0));
    // vocab values without matches (only for small fixed vocabularies)
    if (desc) vocab.forEach((v) => !map.has(v) && map.set(v, 0));
    let list = [...map.entries()].map(([value, count]) => ({ value, count }));
    if (desc) {
      const order = Object.keys(desc);
      list.sort((a, b) => (order.indexOf(a.value) + 1 || 99) - (order.indexOf(b.value) + 1 || 99));
    }
    return list;
  }, [options, selected, vocab, desc]);

  const searchable = merged.length > SEARCHABLE_ABOVE;
  const filtered = query ? merged.filter((o) => String(o.value).toLowerCase().includes(query.toLowerCase())) : merged;
  const visible = showAll || query ? filtered : filtered.slice(0, Math.max(VISIBLE, selected.length));

  const toggle = (v) => {
    const set = new Set(selected);
    if (set.has(v)) set.delete(v);
    else set.add(v);
    onChange([...set]);
  };

  if (!merged.length) return <p className="text-xs text-muted-foreground">No values in these results.</p>;

  return (
    <div className="space-y-1.5">
      {searchable && (
        <div className="relative">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`Search ${FACET_LABELS[facetKey].toLowerCase()}…`} className="h-8 pl-7 text-xs" />
        </div>
      )}
      <ul className={cn('space-y-0.5', (showAll || query) && 'max-h-64 overflow-y-auto pr-1')}>
        {visible.map((o) => {
          const checked = selected.includes(o.value);
          const label = (
            <label className={cn('flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-[13px] hover:bg-muted/60', !o.count && !checked && 'opacity-50')}>
              <Checkbox checked={checked} onCheckedChange={() => toggle(o.value)} />
              {facetKey === 'exit_lane' && <LaneDot lane={o.value} />}
              <span className="min-w-0 flex-1 truncate">{o.value || '(empty)'}</span>
              <span className="text-[11px] tabular-nums text-muted-foreground">{fmtInt(o.count)}</span>
            </label>
          );
          return (
            <li key={o.value}>
              {desc?.[o.value]?.description ? (
                <Hint text={desc[o.value].description} side="right">
                  {label}
                </Hint>
              ) : (
                label
              )}
            </li>
          );
        })}
        {query && !filtered.length && <li className="px-1 py-1 text-xs text-muted-foreground">No match.</li>}
      </ul>
      {!query && filtered.length > VISIBLE && (
        <button type="button" onClick={() => setShowAll((s) => !s)} className="px-1 text-xs font-medium text-muted-foreground hover:text-foreground">
          {showAll ? 'Show less' : `Show all ${filtered.length}`}
        </button>
      )}
    </div>
  );
}

/** Number input that commits on blur / Enter (keeps URL updates calm). */
function NumberField({ label, value, onCommit, suffix, placeholder, min = 0, max }) {
  const [text, setText] = useState(value ?? '');
  useEffect(() => setText(value ?? ''), [value]);
  const commit = () => {
    const t = String(text).trim();
    if (t === String(value ?? '')) return;
    onCommit(t === '' ? undefined : t);
  };
  return (
    <label className="block space-y-1">
      <span className="text-[11px] text-muted-foreground">{label}</span>
      <div className="relative">
        <Input
          type="number"
          inputMode="decimal"
          min={min}
          max={max}
          value={text}
          placeholder={placeholder}
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => e.key === 'Enter' && commit()}
          className={cn('h-8 text-sm tabular-nums', suffix && 'pr-7')}
        />
        {suffix && <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">{suffix}</span>}
      </div>
    </label>
  );
}

export default function FilterPanel({ facets, filters, onChange, vocab = {}, definitions, disabledNote }) {
  const set = (key, value) => {
    const next = { ...filters };
    if (value === undefined || value === null || value === '' || (Array.isArray(value) && !value.length)) delete next[key];
    else next[key] = value;
    onChange(next);
  };
  const total = countFilters(filters);

  return (
    <div className="text-sm">
      <div className="flex items-center justify-between pb-1">
        <h2 className="text-[13px] font-semibold">Filters</h2>
        {total > 0 && (
          <button type="button" onClick={() => onChange({})} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
            <X className="h-3 w-3" /> Clear all ({total})
          </button>
        )}
      </div>
      {disabledNote && <p className="mb-2 rounded-md bg-muted px-2 py-1.5 text-xs text-muted-foreground">{disabledNote}</p>}

      {FACET_ORDER.map((k) => (
        <Group
          key={k}
          title={FACET_LABELS[k]}
          count={filters[k]?.length || 0}
          defaultOpen={OPEN_BY_DEFAULT.includes(k) || !!filters[k]?.length}
          onClear={() => set(k, undefined)}
          info={definitions?.[k] ? <FilterInfo definition={definitions[k]} facetKey={k} className="mr-1" /> : null}
        >
          {!facets ? (
            <div className="space-y-1.5">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-5 animate-pulse rounded bg-muted" />
              ))}
            </div>
          ) : (
            <FacetGroup facetKey={k} options={facets?.[k] || []} selected={filters[k] || []} vocab={vocab[k] || []} onChange={(v) => set(k, v)} />
          )}
        </Group>
      ))}

      <Group title="Numbers" count={['min_qty', 'max_unit_cost', 'min_completeness', 'max_completeness', 'min_age'].filter((k) => filters[k] !== undefined).length}>
        <div className="grid grid-cols-2 gap-2">
          <NumberField label="At least (pieces)" value={filters.min_qty} onCommit={(v) => set('min_qty', v)} placeholder="e.g. 4" />
          <NumberField label="Max purchase price" value={filters.max_unit_cost} onCommit={(v) => set('max_unit_cost', v)} suffix="€" />
          <NumberField label="Product info at least" value={filters.min_completeness} onCommit={(v) => set('min_completeness', v)} suffix="%" max={100} />
          <NumberField label="Product info at most" value={filters.max_completeness} onCommit={(v) => set('max_completeness', v)} suffix="%" max={100} />
          <NumberField label="In stock for at least" value={filters.min_age} onCommit={(v) => set('min_age', v)} suffix="d" placeholder="days" />
        </div>
      </Group>

      <Group title="Flags" info={definitions?.flags ? <FilterInfo definition={definitions.flags} facetKey="flags" className="mr-1" /> : null} count={['supplier_return', 'bulky', 'has_image', 'on_shortlist'].filter((k) => filters[k] !== undefined).length}>
        <div className="space-y-2">
          {['supplier_return', 'bulky', 'has_image', 'on_shortlist'].map((k) => (
            <label key={k} className="flex cursor-pointer items-center justify-between gap-2 text-[13px]">
              {TOGGLE_LABELS[k]}
              <Switch checked={filters[k] === '1'} onCheckedChange={(c) => set(k, c ? '1' : undefined)} />
            </label>
          ))}
        </div>
      </Group>
    </div>
  );
}
